import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Mail, Settings, Send } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const EmailSettingsCRUD = ({ config, onUpdate }) => {
  const [emailSettings, setEmailSettings] = useState({
    mail_driver: 'smtp',
    mail_host: 'email-smtp.us-east-1.amazonaws.com',
    mail_port: 587,
    mail_username: 'AKIA3Y6BA5AENYXAQTM7',
    mail_password: 'BGpaHrhYc6sYyIauymQWy3XyLs6MW0uGleMeEvYf6Q72',
    email_from: 'emailname@kadellabs.com',
    mail_encryption: 'tls',
    admin_email: 'admin@ultralender.ai'
  });
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);

  // Get authentication headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  useEffect(() => {
    if (config.email_settings && config.email_settings.smtp_config) {
      setEmailSettings(config.email_settings.smtp_config);
    }
  }, [config]);

  const updateEmailConfig = async () => {
    setLoading(true);
    try {
      await axios.put(`${API_BASE_URL}/api/admin/config/email_settings/smtp_config`, {
        value: emailSettings,
        data_type: 'json',
        description: 'SMTP email configuration for sending notifications'
      }, {
        headers: getAuthHeaders()
      });
      
      alert('Email configuration updated successfully!');
      onUpdate();
    } catch (error) {
      console.error('Error updating email config:', error);
      alert('Error updating email configuration');
    }
    setLoading(false);
  };

  const testEmailConfig = async () => {
    setTesting(true);
    try {
      // First save the current config
      await updateEmailConfig();
      
      // Send test email
      const response = await axios.post(`${API_BASE_URL}/api/admin/test-email`, {
        test_email: emailSettings.admin_email
      });
      
      if (response.data.success) {
        alert('Test email sent successfully! Check your inbox.');
      } else {
        alert('Failed to send test email. Please check your configuration.');
      }
    } catch (error) {
      console.error('Error testing email:', error);
      alert('Error testing email configuration');
    }
    setTesting(false);
  };

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center">
          <Mail className="h-5 w-5 text-blue-600 mr-2" />
          <h4 className="text-lg font-semibold text-blue-900">Email Configuration</h4>
        </div>
        <p className="text-blue-700 mt-2">
          Configure SMTP settings for sending recommendation confirmation emails to users and notifications to admins.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Mail Driver
          </label>
          <select
            value={emailSettings.mail_driver}
            onChange={(e) => setEmailSettings({...emailSettings, mail_driver: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            disabled={loading}
          >
            <option value="smtp">SMTP</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            SMTP Host
          </label>
          <input
            type="text"
            value={emailSettings.mail_host}
            onChange={(e) => setEmailSettings({...emailSettings, mail_host: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="email-smtp.us-east-1.amazonaws.com"
            disabled={loading}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            SMTP Port
          </label>
          <input
            type="number"
            value={emailSettings.mail_port}
            onChange={(e) => setEmailSettings({...emailSettings, mail_port: parseInt(e.target.value)})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="587"
            disabled={loading}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Encryption
          </label>
          <select
            value={emailSettings.mail_encryption}
            onChange={(e) => setEmailSettings({...emailSettings, mail_encryption: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            disabled={loading}
          >
            <option value="tls">TLS</option>
            <option value="ssl">SSL</option>
            <option value="none">None</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            SMTP Username
          </label>
          <input
            type="text"
            value={emailSettings.mail_username}
            onChange={(e) => setEmailSettings({...emailSettings, mail_username: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="AWS Access Key ID"
            disabled={loading}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            SMTP Password
          </label>
          <input
            type="password"
            value={emailSettings.mail_password}
            onChange={(e) => setEmailSettings({...emailSettings, mail_password: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="AWS Secret Access Key"
            disabled={loading}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            From Email Address
          </label>
          <input
            type="email"
            value={emailSettings.email_from}
            onChange={(e) => setEmailSettings({...emailSettings, email_from: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="noreply@yourdomain.com"
            disabled={loading}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Admin Email (CC Recipient)
          </label>
          <input
            type="email"
            value={emailSettings.admin_email}
            onChange={(e) => setEmailSettings({...emailSettings, admin_email: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="admin@yourdomain.com"
            disabled={loading}
          />
        </div>
      </div>

      <div className="bg-gray-50 rounded-lg p-4">
        <h5 className="font-semibold text-gray-900 mb-2">Email Functionality</h5>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• When someone submits a recommendation, they receive a confirmation email</li>
          <li>• Admin receives a copy (CC) of all recommendation emails</li>
          <li>• Uses professional email templates with Ultralender.ai branding</li>
          <li>• Emails are sent via AWS SES for reliable delivery</li>
        </ul>
      </div>

      <div className="flex space-x-4 pt-4 border-t border-gray-200">
        <button
          onClick={updateEmailConfig}
          disabled={loading}
          className="flex-1 bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </>
          ) : (
            <>
              <Settings className="h-4 w-4 mr-2" />
              Save Configuration
            </>
          )}
        </button>
        
        <button
          onClick={testEmailConfig}
          disabled={testing || loading}
          className="flex-1 bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {testing ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Testing...
            </>
          ) : (
            <>
              <Send className="h-4 w-4 mr-2" />
              Test Email
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default EmailSettingsCRUD;